import { useCallback, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { isEmpty } from 'lodash-es';
import Image from 'next/image';
import type {
  VendorReviewT,
  VendorReviewsResponseT,
} from '@lambda/apis/src/catalog-v2/types';
import i18n from '@/settings/i18n';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import {
  formatGoodReviewsPercentage,
  generateUnitsSoldCount,
  getCountryFlag,
} from '@/components/v2/product/hooks/helpers';
import ReviewCard from '../../../../secondary/reviews/ReviewCard';
import Loading from '@/components/commons/Loading';
import { logger } from '@/lib/logger';
import settings from '@/settings';
import {
  formatCompanyAddress,
  getCountryCode,
  localizeCountryName,
  resolveGoodReviewsPercentage,
  showsLegalDetails,
  showsRegistrationNumber,
} from './helpers';

const t = i18n.init('product');

// Reviews are paged straight off MsCatalog2 — a batch per Load More click.
const REVIEWS_PAGE_SIZE = 4;

const Reviews = () => {
  const { vendorDetails } = useProductPageContext();
  const vendorId = vendorDetails?.name ?? '';

  const [reviews, setReviews] = useState<VendorReviewT[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!vendorId) {
        setLoading(false);

        return;
      }

      setLoading(true);

      try {
        const params = new URLSearchParams({
          vendor: vendorId,
          limit: String(REVIEWS_PAGE_SIZE),
          offset: String(offset),
        });

        const res = await fetch(`/api/v2/reviews/vendor?${params}`);

        if (!res.ok) {
          return;
        }

        const page = (await res.json()) as VendorReviewsResponseT;

        // `offset === 0` is the modal opening; anything else is Load More, so
        // the page is appended rather than replacing what is on screen.
        setReviews((current) =>
          offset === 0 ? page.items : [...current, ...page.items],
        );
        setTotal(page.total);
      } catch (err) {
        logger.error({ err, vendorId }, 'Vendor reviews fetch failed');
      } finally {
        setLoading(false);
      }
    },
    [vendorId],
  );

  // The modal mounts on open, so this is the first page each time it is opened.
  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  // A seller with no reviews shows nothing at all, but only once we know that.
  if (!loading && reviews.length === 0) {
    return null;
  }

  const isFirstPage = loading && reviews.length === 0;
  const hasMore = reviews.length < total;

  if (isFirstPage) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Loading fill="none" />
      </div>
    );
  }

  return (
    <div className="flex-1 px-6 py-4">
      <div className="flex items-center gap-1">
        <h3 className="text-sm font-medium">
          {total > 1 ? t`Seller feedback` : t`Top Review`}
        </h3>
        {total > 1 && (
          <span className="text-xs text-gray-700/75"> ({total})</span>
        )}
      </div>
      <div className="relative mt-4 space-y-4">
        {reviews.map((r) => (
          <ReviewCard
            key={r.id}
            title={r.title}
            // The service names the rating `review`.
            stars={r.review}
            text={r.text}
            createdAt={r.createdAt}
            customerName={r.customerName}
            // A review carries a `skuId` but no product title, storage or
            // colour, so there is no offer line to draw yet.
            isCompact
          />
        ))}
        {hasMore && (
          <button
            type="button"
            className="flex h-10 w-full items-center justify-center rounded border border-gray-700 text-xs font-medium transition duration-150 ease-in-out hover:bg-gray-700 hover:text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => fetchPage(reviews.length)}
            disabled={loading}
          >
            {loading ? <Loading size="xs" /> : t`Load More Reviews`}
          </button>
        )}
      </div>
    </div>
  );
};

const Content = () => {
  const { selectedOffer, vendorDetails } = useProductPageContext();

  const vendorName = selectedOffer?.vendor.name ?? '';

  const company = !isEmpty(vendorDetails.company)
    ? vendorDetails.company
    : null;
  const countryName = company
    ? localizeCountryName(company.country, i18n.getLocale())
    : '';
  // The flag names the vendor's own country, the same country as the name
  // beside it. Vendors outside a Reebelo market have no asset, so the name
  // stands alone.
  const countryFlag = company
    ? getCountryFlag(getCountryCode(company.country))
    : null;
  const showLegalDetails = company != null && showsLegalDetails(settings.store);

  const orderCount = generateUnitsSoldCount(vendorDetails.ordersCount);

  const positiveFeedback = formatGoodReviewsPercentage(
    resolveGoodReviewsPercentage(
      vendorDetails.reviewsCount,
      vendorDetails.goodReviewsPercentage,
    ),
  );

  // "99.8% positive feedback • 25,196 items sold" — either half stands on its
  // own when the other has nothing to show.
  const sellerStats = [
    positiveFeedback &&
      t`${{ percentage: positiveFeedback }}% positive feedback`,
    vendorDetails.ordersCount != null && t`${{ count: orderCount }} items sold`,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <div className="flex h-full flex-col text-sm">
      <div className="border-b border-[#E3E7E5] bg-[linear-gradient(90deg,_rgba(214,_255,_226,_0.50)_0%,_rgba(146,_247,_250,_0.50)_100%)] px-6 py-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold lg:text-xl">{vendorName}</h2>
          <div className="flex h-6 items-center gap-0.5 rounded-full bg-gray-700 px-3 text-xxs font-semibold text-white">
            <Check className="-ml-1 inline h-3.5 w-3.5" strokeWidth={3} />
            <span>{t`Certified Seller`}</span>
          </div>
        </div>
        {sellerStats && (
          <div className="mt-0.5 text-xs font-medium">{sellerStats}</div>
        )}
        {company && (countryFlag || countryName) && (
          <div className="mt-4 flex items-center gap-2 text-xs">
            {countryFlag && (
              <Image
                alt={countryName}
                src={countryFlag}
                className="h-5 w-5 object-contain"
                height={50}
                width={50}
              />
            )}
            {countryName}
          </div>
        )}
      </div>
      <div>
        <Reviews />
      </div>

      {company && showLegalDetails && (
        <div className="mt-auto gap-8 space-y-6 bg-white p-6 text-[#6F8989]">
          <div className="grid grid-cols-2">
            <div>
              <h3 className="font-bold">{t`Legal Name`}</h3>
              <p>{company.name}</p>
            </div>
            {showsRegistrationNumber(settings.store) && (
              <div>
                <h3 className="font-bold">{t`Registration Number`}</h3>
                <p>{company.registrationNumber}</p>
              </div>
            )}
          </div>
          <div>
            <h3 className="font-bold">{t`Address`}</h3>
            <p>{formatCompanyAddress(company)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;
